import {
  type ICreateTournamentUI,
  getBallTypeLabel,
  getMatchTypeLabel,
  getTournamentCategoryLabel,
  type BallType,
  type MatchType,
  type TournamentCategory,
} from '../types/createtournament';
import type { TournamentBannerState } from '../TournamentDetails';
import type { TickerRowState } from '../tournamentsDetails/matches/tickerAutomationConstants';
import type { LocalDivision } from './LocalDivisionsAndTeamsStep';
import './TournamentPreviewStep.scss';

interface Props {
  details: ICreateTournamentUI;
  banner: TournamentBannerState | null;
  divisions: LocalDivision[];
  tickerRows: TickerRowState[];
  onPrevious: () => void;
  onCreate: () => void | Promise<void>;
  isSubmitting: boolean;
}

const Row = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="tp-row">
    <span className="tp-row-label">{label}</span>
    <span className="tp-row-value">{value ?? '—'}</span>
  </div>
);

const FlagPill = ({ on, label }: { on: boolean; label: string }) => (
  <span className={`tp-flag-pill ${on ? 'is-on' : ''}`}>
    <span className="tp-flag-dot" />
    {label}
  </span>
);

function formatDate(d: string): string {
  if (!d) return '—';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function TournamentPreviewStep({
  details,
  banner,
  divisions,
  tickerRows,
  onPrevious,
  onCreate,
  isSubmitting,
}: Props) {
  const enabledTicker = tickerRows.filter((r) => r.automated);
  const totalGroups = divisions.reduce((s, d) => s + d.groups.length, 0);
  const totalTeams = divisions.reduce(
    (sum, d) => sum + d.groups.reduce((s, g) => s + g.teams.length, 0),
    0,
  );

  return (
    <div className="tournament-preview-step">
      <h2 className="create-league-step-title">Review</h2>

      <section className="tp-hero">
        {banner?.previewUrl ? (
          <img className="tp-hero-banner" src={banner.previewUrl} alt={details.tournamentName} />
        ) : (
          <div className="tp-hero-banner tp-hero-banner--empty" aria-hidden />
        )}
        <div className="tp-hero-scrim" aria-hidden />
        <div className="tp-hero-text">
          <span className="tp-hero-badge">
            {getTournamentCategoryLabel(details.tournamentCategory as TournamentCategory)}
          </span>
          <h3>{details.tournamentName || 'Untitled Tournament'}</h3>
          <div className="tp-hero-meta">
            <span>📅 {formatDate(details.startDate)} → {formatDate(details.endDate)}</span>
            <span>🏏 {getMatchTypeLabel(details.matchType as MatchType)}</span>
            <span>🎾 {getBallTypeLabel(details.ballType as BallType)}</span>
          </div>
        </div>
      </section>

      <div className="tp-stat-row">
        <div className="tp-stat-card">
          <span className="tp-stat-value">{divisions.length}</span>
          <span className="tp-stat-label">Divisions</span>
        </div>
        <div className="tp-stat-card">
          <span className="tp-stat-value">{totalGroups}</span>
          <span className="tp-stat-label">Groups</span>
        </div>
        <div className="tp-stat-card">
          <span className="tp-stat-value">{totalTeams}</span>
          <span className="tp-stat-label">Teams</span>
        </div>
        <div className="tp-stat-card">
          <span className="tp-stat-value">{details.maxOversPerBowler ?? '—'}</span>
          <span className="tp-stat-label">Overs / Bowler</span>
        </div>
      </div>

      <div className="tp-grid">
        <section className="tp-card">
          <h4 className="tp-card-title">Organizer</h4>
          <Row label="Name" value={details.organizerName} />
          <Row label="Email" value={details.organizerEmailId} />
          <Row
            label="Phone"
            value={
              details.organizerPhoneNumberCountryCode
                ? `+${details.organizerPhoneNumberCountryCode} ${details.organizerPhoneNumber}`
                : details.organizerPhoneNumber
            }
          />
        </section>

        <section className="tp-card">
          <h4 className="tp-card-title">Scoring Rules</h4>
          <Row label="Overs per Bowler" value={details.maxOversPerBowler} />
          <Row label="Points (Win)" value={details.pointForWin} />
          <Row label="Points (Tie)" value={details.pointForTie} />
          <Row label="Points (Abandoned)" value={details.pointForAbandoned} />
          {details.matchType === 'OTHER_LIMITED_OVER' && (
            <>
              <Row label="Overs" value={details.maxOvers} />
              <Row label="Wickets" value={details.maxWickets} />
            </>
          )}
        </section>

        <section className="tp-card">
          <h4 className="tp-card-title">Rules &amp; Flags</h4>
          <div className="tp-flag-grid">
            <FlagPill on={!!details.isRosterLocked} label="Roster Locked" />
            <FlagPill on={!!details.canEditScoreCard} label="Players Edit Score" />
            <FlagPill on={!!details.isImpactPlayerAllowed} label="Impact Player" />
            <FlagPill on={!!details.bonusPointsEnabled} label="Bonus Points" />
            <FlagPill on={!!details.isStrictModeEnabled} label="Strict Mode" />
            <FlagPill on={!!details.isTickerAutomationEnabled} label="Ticker Automation" />
          </div>
        </section>
      </div>

      <section className="tp-card tp-card--full">
        <h4 className="tp-card-title">
          Divisions ({divisions.length}) &middot; {totalTeams} team{totalTeams === 1 ? '' : 's'}
        </h4>
        {divisions.length === 0 ? (
          <p className="tp-empty">No divisions added.</p>
        ) : (
          <ul className="tp-division-list">
            {divisions.map((d) => (
              <li key={d.id} className="tp-division-item">
                <div className="tp-division-head">
                  <span className="tp-division-name">{d.divisionName}</span>
                  <span className="tp-division-meta">
                    {d.groups.length} group{d.groups.length === 1 ? '' : 's'}
                  </span>
                </div>
                <div className="tp-group-list">
                  {d.groups.map((g) => (
                    <div key={g.id} className="tp-group-block">
                      <div className="tp-group-row">
                        <span className="tp-group-name">{g.name}</span>
                        <span className="tp-group-team-count">
                          {g.teams.length} team{g.teams.length === 1 ? '' : 's'}
                        </span>
                      </div>
                      {g.teams.length > 0 && (
                        <ul className="tp-team-list">
                          {g.teams.map((t) => (
                            <li key={t.id} className="tp-team-chip">
                              {t.logoPreviewUrl ? (
                                <img className="tp-team-logo" src={t.logoPreviewUrl} alt="" />
                              ) : (
                                <span className="tp-team-logo tp-team-logo--placeholder" aria-hidden>
                                  {t.teamName.charAt(0).toUpperCase() || '?'}
                                </span>
                              )}
                              <span className="tp-team-name">{t.teamName}</span>
                              {t.teamCode && <span className="tp-team-code">{t.teamCode}</span>}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {details.isTickerAutomationEnabled && (
        <section className="tp-card tp-card--full">
          <h4 className="tp-card-title">Ticker Automation ({enabledTicker.length})</h4>
          {enabledTicker.length === 0 ? (
            <p className="tp-empty">No ticker graphics enabled.</p>
          ) : (
            <ul className="tp-ticker-list">
              {enabledTicker.map((r) => (
                <li key={r.graphicType} className="tp-ticker-item">
                  <span className="tp-ticker-name">{r.graphicTitle}</span>
                  <span className="tp-ticker-interval">
                    {r.hasInterval ? `every ${r.intervalValue} ${r.intervalType}` : 'On event'}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {details.description && (
        <section className="tp-card tp-card--full">
          <h4 className="tp-card-title">Description</h4>
          <p className="tp-description">{details.description}</p>
        </section>
      )}

      <div className="create-league-step-actions">
        <button
          type="button"
          className="create-league-btn-outline"
          onClick={onPrevious}
          disabled={isSubmitting}
        >
          PREVIOUS
        </button>
        <button
          type="button"
          className="create-league-btn-primary"
          onClick={() => void onCreate()}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Creating…' : 'CREATE'}
        </button>
      </div>
    </div>
  );
}
